import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Dialog, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import InlineBanner from '../components/InlineBanner';
import useWaErrorText from './errorText';

// SmartSend part B — "להסיר את הוואטסאפ מהמבצע?" (owner decision, 2026-09-13).
//
// ⚠️ LEG-ERA ONLY (2026-09-28). Nothing on the units model opens this dialog: the picker creates units
// and never removes one, and a "both" mapping page removes the WhatsApp with "הסר" on its tab, which
// deletes nothing (SmartSendScreen `removeWaAction`). Its one remaining mount is WhatsAppMappingTab's
// leg branch (`showRemove`, false on channel 3). The tile / picker text below describes the leg model.
//
// 🔴 WHAT THIS EXISTS FOR. Unticking the add-on tile on a campaign that ALREADY HAS A LEG and
// pressing "המשך" is a destructive act: the variable mapping the operator built is deleted.
// Until this dialog, that either happened silently or did not happen at all.
//
// ✅ THE CONTRACT HAS CAUGHT UP. v1.21 struck the older sentence ("«המשך» בלי המתג אינו מוחק
// leg") out of §6 and carries the decision itself — «🔴 בוטל v1.21 (§2.3) — ביטול הסימון באריח +
// "המשך" ⇒ `picker.removeConfirm`…» — together with the four i18n keys in the mandatory group.
// Nothing here is a deviation any more. (Until v1.21 this comment said the reversal had not
// reached the frozen document; it has.)
//
// STATE RULE, in full:  ¬checked ∧ HasLeg ∧ "המשך"  ⇒  dialog  ⇒  ON CONFIRM ONLY
//                        DELETE WhatsApp/Mapping/{id}.
// No new route is needed — the DELETE already exists in §5 and is already gated there.
//
// 🔴 AND THE SAFETY GATE (approved with the decision): when the e-mail is already out
// (`EmailCampaignStatus ∈ {2,4}`) the TILE IS LOCKED and this dialog never opens. The server
// answers 409 WA_ALREADY_SENT there in any case — the leg is the evidence key for the report,
// the supervisor mail and "view message" — and a dialog whose only possible outcome is an error
// is pure frustration. That gate lives in `WaAddonCard` (the tile cannot be unticked) and in
// `SmartSendPicker` (which never opens this); it is stated here so the three cannot drift.

interface Props {
    open: boolean;
    busy?: boolean;
    /**
     * 🔴 THE REFUSAL SURFACE. The §4 `Message` of a DELETE that did not happen, or null.
     *
     * Without it this dialog was a dead end: the operator pressed "הסר וואטסאפ", the spinner
     * stopped, the dialog did not change and the button came back to life with nothing said. The
     * reachable trigger is the residual race the v1.21 safety gate cannot cover — the WhatsApp
     * went out between the click and the call (409 `WA_ALREADY_SENT`), or the transport dropped.
     * The gate itself is correct: with `EmailCampaignStatus ∈ {2,4}` the tile is locked and this
     * dialog never opens at all.
     */
    error?: string | null;
    onConfirm: () => void;
    onCancel: () => void;
}

const WaRemoveConfirmDialog: React.FC<Props> = ({
    open, busy = false, error = null, onConfirm, onCancel,
}) => {
    const { t } = useTranslation();
    const { waErrorText } = useWaErrorText();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    return (
        // `dir` is mandatory, not decoration: MUI v4 Dialogs portal into document.body, outside
        // App's <div dir> (App.js:1018), and <html dir> is permanently "ltr" (App.js:727-730
        // stamps it at mount from i18n.language, which is still the 'en' default at that
        // moment). Same treatment as every other dialog on this screen.
        <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                <Typography variant="h6">
                    {t('DataSources.send.whatsapp.picker.removeConfirm.title')}
                </Typography>
            </Box>
            <Box style={{ padding: 24 }}>
                <Typography variant="body2" color="textSecondary">
                    {t('DataSources.send.whatsapp.picker.removeConfirm.body')}
                </Typography>
                {/* `role="alert"`: the operator has already pressed the destructive button and is
                    looking at the buttons, not at the body — the refusal has to interrupt. */}
                {error && (
                    <Box style={{ marginTop: 16 }}>
                        <InlineBanner
                            severity="error"
                            role="alert"
                            size="lg"
                            title={waErrorText(error)}
                        />
                    </Box>
                )}
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                {/* No autoFocus on the destructive button — the same rule the source-switch and
                    send-replace confirms on this screen already follow: a deliberate second
                    click, never an Enter key that lands on delete. */}
                <Button variant="contained" color="primary" disabled={busy} onClick={onConfirm}>
                    {busy
                        ? <CircularProgress size={18} color="inherit" />
                        : t('DataSources.send.whatsapp.picker.removeConfirm.confirm')}
                </Button>
                <Box style={{ flex: 1 }} />
                <Button onClick={onCancel} disabled={busy} autoFocus>
                    {t('DataSources.send.whatsapp.picker.removeConfirm.cancel')}
                </Button>
            </Box>
        </Dialog>
    );
};

export default WaRemoveConfirmDialog;
